import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useQuery, useQueryClient } from 'react-query';
import QuickViewSection from '../../components/QuickViewSection';
import AppointmentCard from '../AppointmentCard';
import {
  getAppointmentsConfig,
  getAppointment,
  getClientAppointments,
  APPOINTMENTS_STATUS,
} from '../../../../../../services/appointments';
import { getEvent } from '../../../../../../services/events';
import AddEditAppointment from '../AddEditAppointment';
import useSearchParams from '../../../../../../hooks/useSearchParams';

const ClientAppointments = ({ clientId, allAssoc = [], setViewAllType }) => {
  const [showAddModal, setShowAddModal] = useState(false);
  const [editAppointment, setEditAppointment] = useState(null);
  const { getValue: getSearchParam } = useSearchParams();
  const appointmentIdParam = getSearchParam('appointmentId');
  const appointmentModalParam = getSearchParam('appointmentModal');
  const queryClient = useQueryClient();

  const { data, isFetching, refetch } = useQuery(
    ['client-appointments', clientId],
    async () => {
      try {
        const query = {
          limit: 5,
          offset: 0,
          statuses: [APPOINTMENTS_STATUS.REQUESTED, APPOINTMENTS_STATUS.ASSIGNED],
        };
        const { data: appointments } = await getClientAppointments(clientId, query);
        return appointments;
      } catch (error) {
        console.error('Error fetching client appointments:', error);
        return [];
      }
    },
    {
      enabled: !!clientId,
      refetchOnWindowFocus: false,
      initialData: [],
    },
  );

  const handleAddAppointment = useCallback(() => {
    refetch();
    queryClient.refetchQueries(['clientRecentAppointments', clientId]);
  }, [queryClient]);

  const handleEditAppointment = useCallback(async appointment => {
    try {
      const response = await getAppointment(appointment.id);
      const fullAppointment = response?.data || response;
      setEditAppointment(fullAppointment);
    } catch (error) {
      console.error('Failed to fetch appointment details:', error);
      setEditAppointment(appointment);
    }
    setShowAddModal(true);
  }, []);

  const { data: appointmentsConfig } = useQuery(
    ['appointments-config'],
    async () => {
      return getAppointmentsConfig();
    },
    {
      refetchOnWindowFocus: false,
      initialData: {
        stores: [],
        appointmentTypes: [],
      },
    },
  );

  const eventIds = useMemo(() => [...new Set(data?.map(a => a.eventId).filter(Boolean))], [data]);

  const { data: eventNames = {} } = useQuery(
    ['event-names', ...eventIds],
    async () => {
      const results = await Promise.all(
        eventIds.map(id => getEvent({ eventId: id, loadingStatusOff: true }).catch(() => null)),
      );
      const map = {};
      results.forEach(event => {
        if (event?.id) map[event.id] = event.name;
      });
      return map;
    },
    {
      enabled: eventIds.length > 0,
      refetchOnWindowFocus: false,
    },
  );

  const handleCloseAddAppointment = useCallback(() => {
    setShowAddModal(false);
    setEditAppointment(null);
  }, []);

  useEffect(() => {
    if (data?.length > 0) {
      if (appointmentIdParam && appointmentModalParam === 'true') {
        const appointmentToOpen = data?.find(appointment => appointment.id.toString() === appointmentIdParam);
        if (appointmentToOpen) {
          handleEditAppointment(appointmentToOpen);
        }
      }
    }
  }, [data, appointmentIdParam, appointmentModalParam]);

  return (
    <QuickViewSection
      title="Appointments"
      onViewAllClick={() => {
        setViewAllType('allAppointments');
      }}
      onAddClick={() => setShowAddModal(true)}
      isLoading={isFetching}
    >
      {data?.map(appointment => (
        <AppointmentCard
          key={appointment.id}
          {...appointment}
          allAssoc={allAssoc}
          eventName={appointment.eventId ? eventNames[appointment.eventId] : null}
          onAssignedSuccess={() => {
            refetch();
            queryClient.refetchQueries(['clientRecentAppointments', clientId]);
          }}
          handleEdit={() => handleEditAppointment(appointment)}
          appointmentsConfig={appointmentsConfig}
        />
      ))}
      {data?.length === 0 && !isFetching && (
        <div className="qv-section-empty">No Appointments</div>
      )}
      <AddEditAppointment
        isOpen={showAddModal}
        clientId={clientId}
        onClose={handleCloseAddAppointment}
        onSuccess={handleAddAppointment}
        allAssoc={allAssoc}
        appointment={editAppointment}
        eventId={editAppointment?.event?.id}
        eventName={editAppointment?.event?.name}
        eventStartDate={editAppointment?.event?.startDate}
        eventEndDate={editAppointment?.event?.endDate}
        disableEventSelect={!!editAppointment?.event?.id}
      />
    </QuickViewSection>
  );
};

export default ClientAppointments;
